import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowClockwise } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { SOCIAL_PLATFORMS, MARKETPLACES } from "../platforms";
import { useConnectionHealth } from "../usePromo";
import { StatusDot } from "./StatusBadge";
import { EmptyState } from "./EmptyState";

function buildEntries(sources, accounts) {
  const marketplaces = MARKETPLACES.map((m) => {
    const src = sources.find((s) => s.connector_id === m.key);
    return {
      ...m, kind: "marketplace",
      connected: src?.status === "connected",
      status: src ? src.status : "disconnected",
      meta: src?.name || null,
      lastSync: src?.last_sync_at || null,
    };
  });
  const socials = SOCIAL_PLATFORMS.map((p) => {
    const linked = accounts.filter((a) => a.platform === p.key && a.status === "connected");
    return {
      ...p, kind: "social",
      connected: linked.length > 0,
      status: p.previewOnly ? "info" : linked.length ? "connected" : "disconnected",
      meta: linked.length ? `${linked.length} account` : null,
      lastSync: null,
    };
  });
  return { marketplaces, socials };
}

function fmtSync(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return null;
  }
}

// ── Compact strip (sidebar mini-strip / inline) ──────────────────────────────
export function ConnectionStrip({ className }) {
  const { sources, accounts } = useConnectionHealth();
  const { marketplaces, socials } = useMemo(() => buildEntries(sources, accounts), [sources, accounts]);
  const all = [...marketplaces, ...socials];
  return (
    <div className={cn("flex flex-wrap items-center gap-x-2.5 gap-y-2", className)} data-testid="connection-strip">
      {all.map(({ key, label, Icon, status }) => (
        <span key={key} className="flex items-center gap-1" title={`${label} · ${status}`}>
          <Icon size={13} className="text-muted-foreground" />
          <StatusDot status={status} />
        </span>
      ))}
    </div>
  );
}

// ── Card grid (full page / dashboard block) ─────────────────────────────────
export function ConnectionHealth({ title, showReconnect = true, className }) {
  const { t } = useTranslation();
  const { sources, accounts, loading, reload } = useConnectionHealth();
  const { marketplaces, socials } = useMemo(() => buildEntries(sources, accounts), [sources, accounts]);

  const Section = ({ heading, entries }) => (
    <div className="space-y-3">
      <div className="promo-overline">{heading}</div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {entries.map((e, i) => (
          <motion.div
            key={e.key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: i * 0.04 }}
            className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 transition-colors hover:bg-accent/40"
            data-testid={`connection-card-${e.key}`}
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-accent/40">
              <e.Icon size={18} className="text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium">{e.label}</span>
                <StatusDot status={e.status} />
              </div>
              <div className="mt-0.5 truncate text-xs text-muted-foreground">
                {e.previewOnly
                  ? t("promo.conn.previewOnly")
                  : e.connected
                    ? (e.meta || t("promo.conn.connected")) + (fmtSync(e.lastSync) ? ` · ${fmtSync(e.lastSync)}` : "")
                    : t("promo.conn.disconnected")}
              </div>
            </div>
            {showReconnect && !e.connected && !e.previewOnly && (
              <Button
                asChild variant="outline" size="sm"
                className="h-7 shrink-0 rounded-md border-border text-xs"
                data-testid={`reconnect-${e.key}`}
              >
                <a href={e.kind === "marketplace" ? "/ecommerce" : "/social"}>{t("promo.conn.reconnect")}</a>
              </Button>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={cn("space-y-6", className)} data-testid="connection-health">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium tracking-tight">{title || t("promo.conn.title")}</h3>
        <Button
          variant="ghost" size="sm" onClick={reload}
          className="h-7 gap-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground"
          data-testid="connection-refresh"
        >
          <ArrowClockwise size={13} weight="bold" className={loading ? "animate-spin" : ""} />
          {t("promo.conn.refresh")}
        </Button>
      </div>
      {!loading && !marketplaces.length && !socials.length ? (
        <EmptyState title={t("promo.conn.title")} description={t("promo.conn.empty")} />
      ) : (
        <div className="space-y-6">
          <Section heading={t("promo.conn.marketplaces")} entries={marketplaces} />
          <Section heading={t("promo.conn.social")} entries={socials} />
        </div>
      )}
    </div>
  );
}
